/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { LoginModel } from '../../../../models/components/LoginModel';
import { AuthService } from '../../../../services/AuthService';
import { LoginStore } from '../../../../stores/components/LoginStore';
import * as H from 'history';
import { StoreProvider } from '../../../../stores/StoreProvider';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { ChangeEvent, FormEvent } from 'react';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { Pages } from '../../../../navigation/Pages';
import { AuthUser } from '../../../../types/User';
import { EventStore } from '../../../../stores/application/EventStore';
import { EventTypes } from '../../../../models/application/EventModel';
import { ChangePasswordStore } from '../../../../stores/components/ChangePasswordStore';

export class LoginContentViewModel extends ViewModel {
	private loginForm: LoginModel = new LoginModel();
	private readonly authService: AuthService;
	private readonly loginStore: LoginStore;
	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly eventStore: EventStore;
	private readonly changePasswordStore: ChangePasswordStore;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.loginStore = storeProvider.loginStore;
		this.authService = storeProvider.authService;
		this.snackbarStore = storeProvider.snackbarStore;
		this.authStore = storeProvider.authStore;
		this.eventStore = storeProvider.eventStore;
		this.changePasswordStore = storeProvider.changePasswordStore;
	}

	public goToForgotPassword(): void {
		this.navigateTo(Pages.FORGOT_PASSWORD);
	}

	public closeLoginPopover(): void {
		this.loginStore.closeLoginPopover();
	}

	public changeEmail = (event: ChangeEvent<HTMLInputElement>): void => {
		return this.loginForm.changeEmail(event);
	};

	public changePassword = (event: ChangeEvent<HTMLInputElement>): void => {
		return this.loginForm.changePassword(event);
	};

	public disableLoginButton(): boolean {
		return (
			this.loginForm.email === '' ||
			this.loginForm.password === '' ||
			!this.loginForm.email.match(
				/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
			)
		);
	}

	public async login(e: FormEvent): Promise<void> {
		e.preventDefault();
		try {
			const user: AuthUser = await this.authService.login(this.loginForm.email, this.loginForm.password);
			this.authStore.saveUser(user);
			this.eventStore.addEvent(EventTypes.USER_LOGIN);
			this.loginStore.closeLoginPopover();
			this.navigateTo(Pages.COURSES_OVERVIEW);
			this.changePasswordStore.setDidResetPassword(false);
		} catch (error) {
			if (error.message === 'Network Error') {
				this.snackbarStore.showError('application.network_error');
			} else if (error.request.status === 400) {
				this.snackbarStore.showError('requestAccess.login.credentials_error');
			}
		}
	}
}
